import useLanguageTranslate from "../../../hooks/useLanguageTranslate";

const TravelDetailsTable = ({
  travelData,
  className = "",
  innerClassName="",
  showEditButton = false,
  onEdit = null,
}) => {
  const translate = useLanguageTranslate();

  if (!travelData) return null;

  // Build table rows dynamically based on available data
  const buildTableRows = () => {
    const rows = [];

    // Check if this is travel-specific data (has predefined fields) or generic key-value data
    const hasPassengerData = Object.keys(travelData).some(
      (key) => key.startsWith("passenger") || key.includes("passenger")
    );
    const hasSpecificFields =
      hasPassengerData || travelData.departureDate || travelData.plan;

    if (hasSpecificFields) {
      // Handle travel-specific data format (existing logic)
      // Dynamically add all passengers
      Object.keys(travelData)
        .filter(
          (key) => key.startsWith("passenger") || key.includes("passenger")
        )
        .forEach((passengerKey, index) => {
          if (travelData[passengerKey]) {
            const passengerNumber = index + 1;
            rows.push({
              label: translate(
                `Passenger ${passengerNumber}`,
                ` مسافر ${passengerNumber}`
              ),
              value: travelData[passengerKey],
            });
          }
        });

      // Add date rows — annual policy shows duration + start date instead of departure/return
      if (travelData.policyType_annual) {
        rows.push({
          label: translate("Policy Duration", "مدة الوثيقة"),
          value: translate("Annual (1 Year)", "سنوية (سنة واحدة)"),
        });
        if (travelData.startDate) {
          rows.push({
            label: translate("Start Date", "تاريخ البدء"),
            value: travelData.startDate,
          });
        }
      } else {
        if (travelData.departureDate) {
          rows.push({
            label: translate("Departure date", "تاريخ المغادرة"),
            value: travelData.departureDate,
          });
        }

        if (travelData.returnDate) {
          rows.push({
            label: translate("Return date", "تاريخ العودة"),
            value: travelData.returnDate,
          });
        }
      }

      if (travelData.plan) {
        // Translate plan names to Arabic
        const getPlanTranslation = (planName) => {
          const planTranslations = {
            Silver: translate("Silver", "الفضية"),
            Gold: translate("Gold", "الذهبية"),
            Platinum: translate("Platinum", "البلاتينية"),
            Schengen: translate("Schengen", "شنغن"),
            Worldwide: translate("Worldwide", "جميع دول العالم"),
            "Worldwide (excl. USA & Canada)": translate("Worldwide (excl. USA & Canada)", "جميع دول العالم (باستثناء الولايات المتحدة الأمريكية وكندا)"),
          };
          return planTranslations[planName] || planName;
        };

        rows.push({
          label: translate("Plan", "الخطة"),
          value: getPlanTranslation(travelData.plan),
        });
      }

      if (travelData.addons && travelData.addons.length > 0) {
        rows.push({
          label: translate("Add-ons", "الإضافات"),
          value: travelData.addons,
          isMultiple: true,
        });
      }
    } else {
      // Handle generic key-value data format
      Object.entries(travelData).forEach(([key, value]) => {
        // Always show Date of Birth field, even when empty
        const isDateOfBirth =
          key.includes("Date of Birth") || key.includes("تاريخ الميلاد");

        if (
          isDateOfBirth ||
          (value && value !== "" && value !== null && value !== undefined)
        ) {
          rows.push({
            label: key, // Key is already translated
            value: isDateOfBirth && (!value || value === "") ? "" : value,
            isMultiple: Array.isArray(value),
          });
        }
      });
    }

    return rows;
  };

  const tableRows = buildTableRows();

  return (
    <div
      className={`bg-white border border-[#E5E7EB] rounded-[10px] overflow-hidden ${className}`}
    >
      <div className={innerClassName}>
        {tableRows.map((row, index) => {
          return (
            <div
              key={index}
              className={`flex ${
                index !== tableRows.length - 1 ? "border-b border-[#E5E7EB]" : ""
              }`}
            >
              <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l rtl:border-r-0 min-h-[44px] leading-[44px]">
                <span className="text-[#5B6871] text-sm font-medium">
                  {row.label}
                </span>
              </div>
              <div className="flex-1 bg-white min-h-[44px] h-auto leading-[44px]">
                {row.isMultiple && Array.isArray(row.value) ? (
                  <div>
                    {row.value.map((item, idx) => (
                      <div key={idx}>
                        <div className="px-4 text-[#5B6871] text-sm font-medium min-h-[44px] h-auto leading-[44px]">
                          {item}
                        </div>
                        {idx < row.value.length - 1 && (
                          <hr className="border-t border-[#E5E7EB]" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4">
                    <span className="text-[#1A2024] text-sm font-medium">
                      {row.value}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TravelDetailsTable;
